/**
 * Employee Time Portal — Time Off (P-TAS-20 Phase 3, App-3; handoff §8, screenshots 37–43).
 * The pure rules behind the request form, the balance cards, the request history and the
 * ledger. No React / axios imports, so jest covers them directly.
 *
 * The request rules mirror the server's validator (API src/db/timeOff/timeOffMobileValidation.ts)
 * and the mobile SubmitTimeOffModal, as a PRE-FLIGHT only — the API stays the authority and its
 * 400 message is always shown:
 *   - every weekday between the first and last day is requested (holidays skipped);
 *   - weekend / holiday days only as optional rows, when the tenant and API both allow it;
 *   - hours in the tenant's step (mobile timeOffHourIncrement, default 2), ≤ 8 a day, > 0;
 *   - Unpaid Time: weekdays only, full 8-hour days only;
 *   - not before requestOptions.earliestRequestDate, not on a blocked (submitted) past day,
 *     not before the category's eligibleOn, at most 365 days out.
 *
 * Balances: the API deducts a request's hours from `currentBalanceHours` WHEN IT IS SUBMITTED
 * (and refunds them on deny / cancel), so "Available" is already net of pending requests and
 * the projected balance is simply available − this request. Subtracting `pendingHours` again
 * would double-count and refuse requests the server accepts.
 */
import { describeBlocking, isUnpaid, UNPAID_DAY_HOURS } from '../../../util/timeOffUnpaid';
import type {
  PortalHoliday,
  PortalTimeOffBalance,
  PortalTimeOffBlocking,
  PortalTimeOffRequest,
  PortalTimeOffRequestBody,
} from './portalTypes';
import { addDaysYmd, fmtYmdDow, fmtYmdShort } from './portalTime';

export const MAX_DAY_HOURS = 8;
export const HALF_DAY_HOURS = 4;
export const DEFAULT_HOUR_INCREMENT = 2;
/** Longest range the form expands into rows (the admin modal's limit). */
export const MAX_RANGE_DAYS = 92;
/** The API refuses a first day more than this far out (TOO_FAR_AHEAD). */
export const MAX_DAYS_AHEAD = 365;

/** Two-decimal hours, the portal's format: 8 → "8.00". */
export function f2(h: number | null | undefined): string {
  const n = Number(h);
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

/* ------------------------------------------------------------------ hours per day */

/** Mobile resolveHourIncrement: a positive step that divides 8 evenly, else 2. */
export function resolveHourIncrement(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= MAX_DAY_HOURS
    && Number.isInteger(MAX_DAY_HOURS / value)
    ? value
    : DEFAULT_HOUR_INCREMENT;
}

/** The custom-hours choices: increment, 2×increment … 8. */
export function hourOptions(increment: number): number[] {
  const inc = resolveHourIncrement(increment);
  const steps = Math.round(MAX_DAY_HOURS / inc);
  return Array.from({ length: steps }, (_, i) => Math.round((i + 1) * inc * 100) / 100);
}

/** Half day (4 h) is offered only when it is a valid step for the tenant. */
export function halfDayAllowed(increment: number): boolean {
  return hourOptions(increment).includes(HALF_DAY_HOURS);
}

/** Snap a typed / stored value onto the tenant's steps (nearest, 1 step minimum, 8 max). */
export function snapHours(h: number, increment: number): number {
  const opts = hourOptions(increment);
  if (!Number.isFinite(h)) return opts[opts.length - 1];
  return opts.reduce((best, o) => (Math.abs(o - h) < Math.abs(best - h) ? o : best), opts[0]);
}

/** Full / Half / Custom; `off` is the default of an optional weekend / holiday row (0 h, not sent). */
export type DayMode = 'full' | 'half' | 'custom' | 'off';

export interface RequestDay {
  ymd: string;
  /** A weekend or company-holiday row the employee MAY include (allowWeekendHolidayDays). */
  optional: boolean;
  /** The holiday's name when the day is a company holiday. */
  holidayName: string | null;
  weekend: boolean;
}

/** Hours a row contributes under its mode. Unpaid Time is always a full 8-hour day. */
export function hoursForDay(
  day: Pick<RequestDay, 'optional'>,
  mode: DayMode | undefined,
  customHours: number | undefined,
  opts: { unpaid: boolean; increment: number },
): number {
  if (opts.unpaid) return day.optional ? 0 : UNPAID_DAY_HOURS;
  const m = mode ?? defaultMode(day);
  switch (m) {
    case 'off':
      return 0;
    case 'half':
      return HALF_DAY_HOURS;
    case 'custom':
      return snapHours(customHours ?? HALF_DAY_HOURS, opts.increment);
    case 'full':
    default:
      return MAX_DAY_HOURS;
  }
}

/** A new row's mode: a full day on a workday, nothing on an optional weekend / holiday. */
export function defaultMode(day: Pick<RequestDay, 'optional'>): DayMode {
  return day.optional ? 'off' : 'full';
}

/* ------------------------------------------------------------------ the date range */

function dowOf(ymd: string): number {
  return new Date(`${ymd.slice(0, 10)}T00:00:00Z`).getUTCDay();
}

export function isWeekendYmd(ymd: string): boolean {
  const d = dowOf(ymd);
  return d === 0 || d === 6;
}

/** Calendar days in [from, to] inclusive; 0 when empty or backwards. */
export function rangeSpanDays(fromYmd: string, toYmd: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromYmd) || !/^\d{4}-\d{2}-\d{2}$/.test(toYmd) || toYmd < fromYmd) return 0;
  const a = Date.parse(`${fromYmd}T00:00:00Z`);
  const b = Date.parse(`${toYmd}T00:00:00Z`);
  return Math.round((b - a) / 86400000) + 1;
}

/**
 * The rows for [from, to]: every weekday that isn't a company holiday, plus — only when
 * `includeOptional` — weekend and holiday days as optional rows. An over-long range returns
 * nothing (the form names that case on its own).
 */
export function expandRequestDays(
  fromYmd: string,
  toYmd: string,
  holidays: Map<string, string>,
  includeOptional: boolean,
): RequestDay[] {
  const span = rangeSpanDays(fromYmd, toYmd);
  if (span === 0 || span > MAX_RANGE_DAYS) return [];
  const out: RequestDay[] = [];
  for (let i = 0; i < span; i++) {
    const ymd = addDaysYmd(fromYmd, i);
    const weekend = isWeekendYmd(ymd);
    const holidayName = holidays.get(ymd) ?? null;
    const optional = weekend || holidayName != null;
    if (optional && !includeOptional) continue;
    out.push({ ymd, optional, holidayName, weekend });
  }
  return out;
}

/** The first weekday after `ymd` (the form's default start). */
export function nextWorkday(ymd: string, holidays: Map<string, string> = new Map()): string {
  let d = addDaysYmd(ymd, 1);
  for (let i = 0; i < 14 && (isWeekendYmd(d) || holidays.has(d)); i++) d = addDaysYmd(d, 1);
  return d;
}

/* ------------------------------------------------------------------ categories & balances */

/**
 * The request form's starting category: the first one usable TODAY — requestable, paid,
 * eligible (not waiting on tenure) and with hours available — so the form never opens on
 * e.g. Sick "can be used from Nov 30" with a negative projection. Then relaxes: requestable
 * paid + eligible, requestable paid with hours, requestable paid, anything requestable, the first.
 */
export function defaultCategory<T extends Pick<PortalTimeOffBalance, 'isRequestable' | 'isEligible' | 'currentBalanceHours' | 'isUnpaid'>>(cats: T[]): T | null {
  if (!cats.length) return null;
  const requestable = (c: T) => c.isRequestable !== false;
  const paid = (c: T) => !isUnpaid(c);
  const eligible = (c: T) => c.isEligible !== false;
  const hasHours = (c: T) => (Number(c.currentBalanceHours) || 0) > 0;
  return (
    cats.find((c) => requestable(c) && paid(c) && eligible(c) && hasHours(c)) ??
    cats.find((c) => requestable(c) && paid(c) && eligible(c)) ??
    cats.find((c) => requestable(c) && paid(c) && hasHours(c)) ??
    cats.find((c) => requestable(c) && paid(c)) ??
    cats.find(requestable) ??
    cats[0]
  );
}

/** Earliest day the category may be requested for: the past-date window, then tenure (eligibleOn). */
export function categoryEarliest(cat: Pick<PortalTimeOffBalance, 'isEligible' | 'eligibleOn'> | null, earliestRequestYmd: string): string {
  if (cat && cat.isEligible === false && cat.eligibleOn && cat.eligibleOn > earliestRequestYmd) return cat.eligibleOn.slice(0, 10);
  return earliestRequestYmd;
}

/** Projected balance after this request. `available` is already net of pending (see the file note). */
export function projectBalance(input: { available: number; requestHours: number; unpaid: boolean }): { after: number | null; short: boolean } {
  if (input.unpaid) return { after: null, short: false };
  const after = Math.round((input.available - input.requestHours) * 100) / 100;
  return { after, short: after < 0 };
}

/** Why a category can't be requested right now (the Unpaid Time gate), or null. Names the blocking categories. */
export function notRequestableReason(cat: Pick<PortalTimeOffBalance, 'isRequestable' | 'notRequestableReason' | 'blocking'> | null): string | null {
  if (!cat || cat.isRequestable !== false) return null;
  if (cat.notRequestableReason === 'PAID_NOT_EXHAUSTED') {
    const lines = (cat.blocking ?? []).filter((b) => b && b.categoryName).map((b) => describeBlocking(b));
    return lines.length
      ? `Available once your paid time off is used up — ${lines.join('; ')}.`
      : 'Available once your paid time off is used up.';
  }
  return 'Not available to request right now.';
}

/** Categories in policy order (sortOrder, then name). */
export function sortCategories<T extends Pick<PortalTimeOffBalance, 'sortOrder' | 'categoryName'>>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.categoryName.localeCompare(b.categoryName));
}

/* ------------------------------------------------------------------ pre-flight */

export interface PreflightInput {
  category: PortalTimeOffBalance | null;
  fromYmd: string;
  toYmd: string;
  todayYmd: string;
  earliestRequestYmd: string;
  blockedPastDates: Set<string>;
  /** The rows with their hours (0-hour optional rows included; they're dropped from the body). */
  days: { ymd: string; hours: number }[];
}

/**
 * The first reason the form can't be submitted, or null. Shown in the form's fixed hint line
 * with Submit disabled. Over-balance is reported by projectBalance, not here.
 */
export function preflightReason(input: PreflightInput): string | null {
  const { category: cat, fromYmd, toYmd } = input;
  if (!cat) return 'Choose a type of time off.';
  const gate = notRequestableReason(cat);
  if (gate) return gate;
  if (!fromYmd || !toYmd) return 'Choose the first and last day.';
  if (toYmd < fromYmd) return 'The last day is before the first day.';
  if (rangeSpanDays(fromYmd, toYmd) > MAX_RANGE_DAYS) return `That range is longer than ${MAX_RANGE_DAYS} days — check the dates.`;
  if (fromYmd > addDaysYmd(input.todayYmd, MAX_DAYS_AHEAD)) return 'The first day can be at most a year from today.';
  const earliest = categoryEarliest(cat, input.earliestRequestYmd);
  if (fromYmd < input.earliestRequestYmd) return `Requests can start on ${fmtYmdDow(input.earliestRequestYmd)} at the earliest.`;
  if (fromYmd < earliest) return `${cat.categoryName} can be used from ${fmtYmdDow(earliest)} — start on or after that day.`;
  const sent = input.days.filter((d) => d.hours > 0);
  if (sent.length === 0) return 'There are no hours to request in this range.';
  const blocked = sent.find((d) => input.blockedPastDates.has(d.ymd));
  if (blocked) return `${fmtYmdDow(blocked.ymd)} is in a week whose timesheet was already submitted. Choose other dates.`;
  if (!isUnpaid(cat) && cat.annualRemainingHours != null) {
    const year = input.todayYmd.slice(0, 4);
    const thisYear = sent.filter((d) => d.ymd.slice(0, 4) === year).reduce((s, d) => s + d.hours, 0);
    if (thisYear > cat.annualRemainingHours + 1e-9) {
      return `That's more than the ${f2(cat.annualRemainingHours)} h of ${cat.categoryName} you can still use this year.`;
    }
  }
  return null;
}

/** The POST body: 0-hour (optional) rows are never sent; the note is trimmed. */
export function buildRequestBody(categoryID: number, days: { ymd: string; hours: number }[], notes: string): PortalTimeOffRequestBody {
  return {
    timeOffCategoryID: categoryID,
    days: days.filter((d) => d.hours > 0).map((d) => ({ date: d.ymd, hours: d.hours })),
    notes: notes.trim(),
  };
}

/* ------------------------------------------------------------------ submit / cancel refusals */

/** The 400 body of POST /timeOff/requests (all but `message` are additive). */
export interface TimeOffRefusal {
  message?: string | null;
  code?: string | null;
  dates?: string[] | null;
  blocking?: PortalTimeOffBlocking[] | null;
}

/**
 * The text for a refused submit. The server's message is user-facing already; the Unpaid Time
 * codes add what it leaves out — the paid categories still holding time, or the clashing dates.
 */
export function submitRefusalText(data: TimeOffRefusal | null | undefined, fallback: string): string {
  const base = (data?.message ?? '').trim() || fallback;
  if (data?.code === 'PAID_NOT_EXHAUSTED') {
    const lines = (Array.isArray(data.blocking) ? data.blocking : []).map((b) => describeBlocking(b));
    return lines.length ? `${base} ${lines.join('; ')}.` : base;
  }
  if ((data?.code === 'OVERLAPS_OTHER_TIME_OFF' || data?.code === 'DAY_HAS_PUNCHES') && Array.isArray(data.dates) && data.dates.length) {
    const named = data.dates.map((d) => fmtYmdDow(d)).filter(Boolean);
    const missing = data.dates.some((d) => !base.includes(d));
    return missing && named.length ? `${base} (${named.join(', ')})` : base;
  }
  return base;
}

/* ------------------------------------------------------------------ request history */

export type RequestStatus = 'pending' | 'approved' | 'denied' | 'cancelled' | 'other';

export function requestStatus(status: string | null | undefined): RequestStatus {
  const s = (status ?? '').toUpperCase();
  if (s === 'PENDING') return 'pending';
  if (s === 'APPROVED') return 'approved';
  if (s === 'DENIED' || s === 'DENIED_AFTER_APPROVAL') return 'denied';
  if (s === 'CANCELLED' || s === 'CANCELED') return 'cancelled';
  return 'other';
}

export const STATUS_PILL: Record<RequestStatus, { label: string; tone: 'warn' | 'ok' | 'danger' | 'neutral' }> = {
  pending: { label: 'Pending', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  denied: { label: 'Denied', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  other: { label: 'Updated', tone: 'neutral' },
};

/** Request days with hours (falls back to the range when the API sent no breakdown). */
export function requestDays(r: PortalTimeOffRequest): { date: string; hours: number }[] {
  return Array.isArray(r.days) ? r.days.map((d) => ({ date: d.date.slice(0, 10), hours: Number(d.hours) || 0 })) : [];
}

/** "3 days (partial)" — partial when any day is under 8 h. */
export function daysLabel(r: PortalTimeOffRequest): string {
  const days = requestDays(r);
  const n = days.length || 1;
  const partial = days.some((d) => d.hours < MAX_DAY_HOURS);
  return `${n} day${n === 1 ? '' : 's'}${partial ? ' (partial)' : ''}`;
}

/** "Mon Aug 10" or "Aug 10 – Fri Aug 14". */
export function rangeLabel(startYmd: string, endYmd: string): string {
  const a = startYmd.slice(0, 10);
  const b = endYmd.slice(0, 10);
  return a === b ? fmtYmdDow(a) : `${fmtYmdShort(a)} – ${fmtYmdDow(b)}`;
}

export type CancelState =
  | { kind: 'none' }
  | { kind: 'ok' }
  | { kind: 'locked'; why: string };

/**
 * Whether a row offers Cancel. Only pending / approved requests can be cancelled, and not once a
 * requested day sits in a submitted week (requestOptions.blockedPastDates) or the server has
 * already refused (its message, kept from the last attempt). An exported week isn't knowable
 * here — the server's 400 covers it.
 */
export function cancelState(r: PortalTimeOffRequest, blockedPastDates: Set<string>, refusedMessage?: string | null): CancelState {
  const st = requestStatus(r.status);
  if (st !== 'pending' && st !== 'approved') return { kind: 'none' };
  if (refusedMessage) return { kind: 'locked', why: 'Week locked' };
  if (requestDays(r).some((d) => blockedPastDates.has(d.date))) return { kind: 'locked', why: 'Week submitted' };
  return { kind: 'ok' };
}

/* ------------------------------------------------------------------ holidays */

export interface HolidayView {
  next: PortalHoliday | null;
  /** True when the next holiday is paid for this employee (or the API gave no verdict). */
  eligible: boolean;
  /** Tenure: the day holiday pay starts (YYYY-MM-DD), when that is what blocks it. */
  eligibleAfter: string | null;
  /** 0–100, progress through the tenure wait (only with eligibleAfter). */
  tenurePct: number;
  /** Why the next holiday isn't paid, in words. */
  reason: string | null;
}

/** One holiday's verdict in words: null when paid. */
export function holidayReason(h: PortalHoliday): string | null {
  const e = h.eligibility;
  if (!e || e.isEligible) return null;
  if (e.reason === 'tenure') {
    const on = e.tenure?.eligibleOn;
    return on ? `Holiday pay starts ${fmtYmdDow(on)}` : 'Not eligible yet';
  }
  if (e.reason === 'dayBefore') return e.dayBefore?.date ? `Must work ${fmtYmdDow(e.dayBefore.date)}` : 'Must work the day before';
  if (e.reason === 'dayAfter') return e.dayAfter?.date ? `Must work ${fmtYmdDow(e.dayAfter.date)}` : 'Must work the day after';
  return 'Not eligible';
}

/** The Holiday balance card: the next holiday on/after today and its eligibility. */
export function holidayView(holidays: PortalHoliday[], todayYmd: string): HolidayView {
  const upcoming = holidays
    .filter((h) => (h.holidayDate ?? '').slice(0, 10) >= todayYmd)
    .sort((a, b) => (a.holidayDate < b.holidayDate ? -1 : a.holidayDate > b.holidayDate ? 1 : 0));
  const next = upcoming[0] ?? null;
  if (!next) return { next: null, eligible: true, eligibleAfter: null, tenurePct: 0, reason: null };
  const e = next.eligibility;
  const eligible = !e || e.isEligible;
  let eligibleAfter: string | null = null;
  let tenurePct = 0;
  if (!eligible && e?.reason === 'tenure' && e.tenure?.eligibleOn) {
    eligibleAfter = e.tenure.eligibleOn.slice(0, 10);
    const days = e.tenure.eligibilityDays;
    if (days && days > 0) {
      const left = rangeSpanDays(todayYmd, eligibleAfter) - 1;
      tenurePct = Math.max(4, Math.min(96, ((days - Math.max(0, left)) / days) * 100));
    }
  }
  return { next, eligible, eligibleAfter, tenurePct, reason: eligible ? null : holidayReason(next) };
}

/* ------------------------------------------------------------------ ledger */

const LEDGER_KIND: Record<string, { label: string; tone: 'ok' | 'neutral' | 'warn' | 'info' }> = {
  accrual: { label: 'Accrual', tone: 'ok' },
  usage: { label: 'Used', tone: 'neutral' },
  cashout: { label: 'Cash out', tone: 'neutral' },
  carryover: { label: 'Carryover', tone: 'info' },
  adjustment: { label: 'Adjustment', tone: 'info' },
  pending: { label: 'Pending', tone: 'warn' },
  other: { label: 'Other', tone: 'neutral' },
};

export function ledgerKind(kind: string | null | undefined): { label: string; tone: 'ok' | 'neutral' | 'warn' | 'info' } {
  return LEDGER_KIND[kind ?? 'other'] ?? LEDGER_KIND.other;
}

/** "Showing 26–50 of 112". */
export function pageLabel(offset: number, count: number, total: number): string {
  if (total === 0 || count === 0) return 'No entries';
  return `Showing ${offset + 1}–${offset + count} of ${total}`;
}
